import type { Feature, FeatureCollection, Polygon, Position } from "geojson";
import centroid from "@turf/centroid";
import { Geo } from "@rogoag/geo";
import { isPolygonValid } from "@domain/Geo/geometry";

/** Coordinates round-trip through the Airtable text at 6 decimal places — ~0.1 m, the same
 *  precision `PullInPoint` uses, and enough that parse → format is lossless for real values. */
const COORD_PRECISION = 6;

/** Douglas–Peucker tolerance for a DRAWN ring, in METERS — `Geo.simplifyFeatureCollection`
 *  projects to UTM internally and takes `tolerance` as meters of slack (perpendicular distance
 *  a vertex may lie from its simplified segment before it's retained), not degrees. ≈ 2 m keeps
 *  a click-by-click ring's corners untouched while thinning a shift-drag freehand stroke (points
 *  every 8 px) of its jitter. */
const PULLIN_ZONE_SIMPLIFY_TOLERANCE_M = 2;

export interface PullInZoneProperties {
	name: "pullin";
}

export type PullInZoneResult = { ok: true; zone: PullInZone } | { ok: false; error: string };

/**
 * A pull-in zone — one simple polygon a dispatcher drew for a cluster, which Mission Control
 * seeds into the mission as a generic (`ZoneType.PULLIN`) zone: a bridge the robot may route
 * through.
 *
 * Immutable, equality by value (`key()`), self-validating: the constructor rejects an open ring,
 * fewer than 4 positions, out-of-range or non-finite coordinates, a ring collapsed to fewer than
 * 3 distinct consecutive vertices, and a self-intersecting ring — so a `PullInZone` that exists
 * is always a polygon MC's own zone validation would accept. `fromDrawnRing` and `fromFeature`
 * are the lenient doors: a hand-drawn ring and a free-text Airtable field are both user data, not
 * programming errors, so they return a result / null instead of throwing.
 *
 * Every entry point validates the ROUNDED ring — the one that ends up in `this.ring` — never the
 * caller's raw coordinates. Two vertices that are distinct at full precision can land on the same
 * 6-dp coordinate (e.g. a hairline out-and-back spike a few cm wide); validating pre-rounding
 * coordinates and then storing different, post-rounding ones would let that degenerate ring slip
 * past every check.
 *
 * Axis order: GeoJSON `[lng, lat]` throughout (MC flips to its own `[lat, lon]` at the seam).
 */
export class PullInZone {
	/** Closed (first position repeated last), `[lng, lat]`, rounded to 6 dp. */
	readonly ring: readonly Position[];

	constructor(ring: readonly Position[]) {
		const rounded = roundRing(ring);
		const error = PullInZone.validate(rounded);
		if (error) throw new RangeError(error);
		this.ring = rounded;
	}

	/** Why `ring` is not a pull-in zone, in the words the confirm dialog shows — or null. Callers
	 *  always pass the ROUNDED ring (the one that will actually be stored), never raw input. */
	private static validate(ring: readonly Position[]): string | null {
		if (ring.length < 4) return "Draw at least three vertices.";
		for (const p of ring) {
			if (!Number.isFinite(p[0]) || !Number.isFinite(p[1])) return "The zone has an invalid coordinate.";
			if (Math.abs(p[1]) > 90 || Math.abs(p[0]) > 180) return "The zone has a coordinate outside the map.";
		}
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) return "The zone's ring is not closed.";
		if (distinctConsecutiveVertexCount(ring) < 3) return "The zone collapsed to a line. Draw a larger area.";
		if (!isPolygonValid(toFeature(ring)).isValid) return "The zone crosses itself. Redraw it without crossing edges.";
		return null;
	}

	/**
	 * The drawing tool's door. The engine hands over a closed ring (first vertex repeated last);
	 * the ring is simplified first (freehand jitter), rounded, then validated on the rounded
	 * result — the exact ring the constructor will store — so this never throws, including for a
	 * ring that only fails once rounding collapses it.
	 */
	static fromDrawnRing(ring: readonly Position[]): PullInZoneResult {
		const open = closesItself(ring) ? ring.slice(0, -1) : [...ring];
		if (open.length < 3) return { ok: false, error: "Draw at least three vertices." };
		const simplified = simplifyRing(open);
		if (simplified.length < 3) return { ok: false, error: "The zone is too small to keep. Draw a larger area." };
		const rounded = roundRing([...simplified, simplified[0]]);
		const error = PullInZone.validate(rounded);
		if (error) return { ok: false, error };
		return { ok: true, zone: new PullInZone(rounded) };
	}

	/** The committed field's door: a single-ring `Polygon` Feature, or null for anything else.
	 *  Validates the ROUNDED ring (never throws for a ring that only fails once rounded). */
	static fromFeature(feature: Feature): PullInZone | null {
		const geometry = feature.geometry;
		if (!geometry || geometry.type !== "Polygon") return null;
		if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length !== 1) return null;
		const ring = geometry.coordinates[0];
		if (!Array.isArray(ring) || !ring.every((p) => Array.isArray(p) && p.length >= 2)) return null;
		const rounded = roundRing(ring);
		if (PullInZone.validate(rounded)) return null;
		return new PullInZone(rounded);
	}

	/** The wire shape of one zone inside `Jobs."Map Features GeoJSON (code)"`. */
	toFeature(): Feature<Polygon, PullInZoneProperties> {
		return {
			type: "Feature",
			properties: { name: "pullin" },
			geometry: { type: "Polygon", coordinates: [this.ring.map((p) => [p[0], p[1]])] },
		};
	}

	/** Value identity: the canonical ring text. Several jobs sharing one drawing share one key,
	 *  which is how the map renders a cluster's zone once, badged with the job count. */
	key(): string {
		return this.ring.map((p) => `${p[0]},${p[1]}`).join(";");
	}

	areaAcres(): number {
		const fc: FeatureCollection = { type: "FeatureCollection", features: [this.toFeature()] };
		return Geo.area(fc, "acres");
	}

	/** For the far-from-selection check, in the `{ lat, lng }` shape `isFarFromSelection` takes. */
	centroid(): { lat: number; lng: number } {
		const [lng, lat] = centroid(this.toFeature()).geometry.coordinates;
		return { lat, lng };
	}

	equals(other: PullInZone): boolean {
		return this.key() === other.key();
	}
}

function round(n: number): number {
	return Number(n.toFixed(COORD_PRECISION));
}

/** Round every position in a ring to 6 dp — the transform applied before both storing a ring
 *  and validating it, so the two never disagree. */
function roundRing(ring: readonly Position[]): Position[] {
	return ring.map((p) => [round(p[0]), round(p[1])]);
}

function closesItself(ring: readonly Position[]): boolean {
	if (ring.length < 2) return false;
	const first = ring[0];
	const last = ring[ring.length - 1];
	return first[0] === last[0] && first[1] === last[1];
}

/** How many vertices remain once consecutive duplicates are collapsed, walking the ring's open
 *  portion (the closing repeat of the first vertex is expected and dropped before counting) and
 *  wrapping around (the edge from the last open vertex back to the first counts too). Rounding
 *  can turn two vertices that were distinct when drawn into the same coordinate — e.g. a hairline
 *  out-and-back spike a few cm wide — so a ring must have at least 3 genuinely distinct
 *  neighboring vertices to be a polygon at all. */
function distinctConsecutiveVertexCount(ring: readonly Position[]): number {
	const open = ring.slice(0, -1);
	let count = 0;
	for (let i = 0; i < open.length; i++) {
		const prev = open[(i - 1 + open.length) % open.length];
		if (open[i][0] !== prev[0] || open[i][1] !== prev[1]) count++;
	}
	return count;
}

function toFeature(ring: readonly Position[]): Feature<Polygon> {
	return {
		type: "Feature",
		properties: {},
		geometry: { type: "Polygon", coordinates: [ring.map((p) => [p[0], p[1]])] },
	};
}

/** Douglas–Peucker over the open ring (closed for the call, re-opened after). `highestQuality`
 *  skips the radial-distance pre-pass — these rings are short, exactness wins. */
function simplifyRing(open: readonly Position[]): Position[] {
	const fc: FeatureCollection = { type: "FeatureCollection", features: [toFeature([...open, open[0]])] };
	const simplified = Geo.simplifyFeatureCollection(fc, PULLIN_ZONE_SIMPLIFY_TOLERANCE_M, true);
	const polygon = simplified.features[0]?.geometry as Polygon | undefined;
	const ring = polygon?.coordinates?.[0];
	if (!ring || ring.length < 4) return [];
	return ring.slice(0, -1).map((p) => [p[0], p[1]]);
}
